import type { RequiredFieldData } from "../../../shared/baseStore.types";
import { SignupFlowSlug } from "../constants";
import { inABTestGroupFromSingleton } from "../featureFlags";

import {
  resolveFieldValue,
  type PrefilledFieldsStoreState,
} from "./validatePrefilledFields";
import { isFieldPopulated } from "./isFieldPopulated";
import {
  getRequiredFieldsForSlug,
  validateSignUpField,
} from "./validateSignUpField";

/**
 * Reconciles a slug's cached `validatedFields` (which gates the CTA via `isPageComplete`)
 * with current store values, so it isn't left stale by cross-page edits.
 *
 * Re-validates each required field that is populated or already marked valid, adding
 * valid-but-missing fields and clearing stale ones (e.g. a field cleared on checkout under
 * its own slug). Blank, not-yet-validated fields are skipped to avoid errors on first visit.
 */
export function reconcileValidatedFields(
  state: PrefilledFieldsStoreState,
  slug: SignupFlowSlug,
  validatedFieldKeys: readonly string[],
  context?: Pick<RequiredFieldData, "inABTestGroup">,
): void {
  const inABTestGroup = context?.inABTestGroup ?? inABTestGroupFromSingleton;
  const alreadyValidated = new Set(validatedFieldKeys);

  for (const fieldKey of getRequiredFieldsForSlug(slug, { inABTestGroup })) {
    const value = resolveFieldValue(fieldKey, state);
    if (isFieldPopulated(value) || alreadyValidated.has(fieldKey)) {
      validateSignUpField(fieldKey, value, slug);
    }
  }
}
